import { t, useLanguage } from '../../i18n';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, Alert, Platform, ScrollView, StyleSheet, View, Text } from 'react-native';
import { Button, Card, Chip, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { useFocusEffect } from '@react-navigation/native';
import { auth, db } from '../../firebase';
import { Course } from '../../types';
import { useReviewGateContext } from '../../contexts/ReviewGateContext';
import { useGradeGateContext } from '../../contexts/GradeGateContext';
import { getCurrentSemester, isInGradePeriod } from '../../constants/reviewPeriods';
import { useSchedule } from '../../contexts/ScheduleContext';
import { getGradeSubmissionKey, getOfferingKey, offeringDocument } from '../../utils/courseIdentity';

type Props = {
  navigation: any;
};

type EnrolledCourse = Course & {
  reviewed: boolean;
  gradeReported: boolean;
};

const ReviewManagementScreen = ({ navigation }: Props) => {
  useLanguage();
  const theme = useTheme();
  const { reviewCount, requiredCount } = useReviewGateContext();
  const { requiredCount: requiredGradeCount } = useGradeGateContext();
  const [enrolledCourses, setEnrolledCourses] = useState<EnrolledCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProfileComplete, setIsProfileComplete] = useState(true);

  const { userCourses } = useSchedule();
  const { academicYear, semester } = getCurrentSemester();
  const isGradePeriod = isInGradePeriod();

  const fetchData = useCallback(async () => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const uid = auth.currentUser.uid;
      const current = getCurrentSemester();

      const realCourses = userCourses.filter((c) => !c.isCustom);
      if (realCourses.length === 0) {
        setEnrolledCourses([]);
        setLoading(false);
        return;
      }

      const userDoc = await getDoc(doc(db, 'users', uid));
      const userData = userDoc.data();
      const gradeSubmissionSnapshot = await getDocs(collection(db, 'users', uid, 'gradeSubmissions'));
      const gradeSubmissionIds = new Set<string>(gradeSubmissionSnapshot.docs.map((submission) => submission.id));
      
      setIsProfileComplete(!!(userData?.faculty && userData?.year));

      const reviewedMap = new Map<string, boolean>();
      for (const course of realCourses) {
        const reviewDoc = await getDoc(doc(offeringDocument(db, course), 'reviews', uid));
        const offeringKey = getOfferingKey(course);
        if (!reviewDoc.exists()) {
          reviewedMap.set(offeringKey, false);
          continue;
        }
        const reviewData = reviewDoc.data() as { academicYear?: string; semester?: string };
        reviewedMap.set(
          offeringKey,
          reviewData.academicYear === current.academicYear && reviewData.semester === current.semester
        );
      }

      const results = realCourses
        .map((course) => ({
          ...course,
          reviewed: reviewedMap.get(getOfferingKey(course)) ?? false,
          gradeReported: gradeSubmissionIds.has(getGradeSubmissionKey(course, current.semester)),
        }))
        .sort((a, b) => {
          if (a.reviewed === b.reviewed) return 0;
          return a.reviewed ? 1 : -1;
        });

      setEnrolledCourses(results);
    } catch (error) {
      console.error('[ReviewManagementScreen] fetchData failed:', error);
      if (Platform.OS === 'web') {
        window.alert(t("エラー: データの読み込みに失敗しました"));
      } else {
        Alert.alert(t("エラー"), t("データの読み込みに失敗しました"));
      }
    } finally {
      setLoading(false);
    }
  }, [userCourses]);

  useFocusEffect(
    React.useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  const localReviewCount = enrolledCourses.filter((c) => c.reviewed).length;
  const progressRatio = Math.min(localReviewCount / requiredCount, 1);

  const localGradeCount = enrolledCourses.filter((c) => c.gradeReported).length;
  const gradeProgressRatio = Math.min(localGradeCount / requiredGradeCount, 1);

  const isAllComplete = progressRatio >= 1 && (!isGradePeriod || gradeProgressRatio >= 1);

  const handleGoToProfileSettings = () => {
    navigation.navigate('TabRoot', {
      screen: 'Settings',
      params: { openProfileDialog: true },
    });
  };

  const handleProfileIncompleteAlert = () => {
    const message = '学年・学部が未設定のためレビューを投稿できません。設定画面で登録してください。';
    if (Platform.OS === 'web') {
      window.alert(t(message));
      handleGoToProfileSettings();
      return;
    }
    Alert.alert(t("学年・学部が未設定です"), t(message), [
      { text: t("戻る"), style: 'cancel' },
      { text: t("設定へ移動"), onPress: handleGoToProfileSettings },
    ]);
  };

  const completedContainerColor = theme.dark ? '#1B5E20' : '#E8F5E9';
  const completedContentColor = theme.dark ? '#E8F5E9' : '#1B5E20';
  const completedAccentColor = theme.dark ? '#A5D6A7' : '#2E7D32';

  const renderProgressBar = (ratio: number) => (
    <View
      style={[
        styles.progressBarTrack,
        { backgroundColor: theme.dark ? 'rgba(255,255,255,0.24)' : 'rgba(0,0,0,0.16)' },
      ]}
    >
      <View
        style={[
          styles.progressBarFill,
          {
            width: `${ratio * 100}%` as any,
            backgroundColor: ratio >= 1 ? completedAccentColor : theme.colors.primary,
          },
        ]}
      />
    </View>
  );

  const dayText = (course: Course) => {
    const isOutOfGrid =
      !course.period ||
      course.period < 1 ||
      course.period > 6 ||
      course.dayOfWeek === undefined ||
      course.dayOfWeek < 1 ||
      course.dayOfWeek > 5;

    const hasOnDemandTag =
      (course.name || '').includes('オンデマンド') ||
      (course.name || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.room || '').includes('オンデマンド') ||
      (course.room || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
      (course.notes || '').includes('オンデマンド') ||
      (course.notes || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ');

    if (isOutOfGrid || hasOnDemandTag) return 'オンデマンド';
    if (course.dayOfWeek !== undefined && course.period !== undefined) {
      return `${['', '月', '火', '水', '木', '金', '土', '日'][course.dayOfWeek]}曜${course.period}限`;
    }
    return null;
  };

  const renderCourseCard = (course: EnrolledCourse) => (
    <Card
      key={getOfferingKey(course)}
      style={[styles.courseCard, course.reviewed ? { borderLeftWidth: 4, borderLeftColor: completedAccentColor } : {}]}
    >
      <Card.Content>
        <View style={styles.courseHeader}>
          <View style={styles.courseInfo}>
            <Text style={[styles.courseName, { color: theme.colors.onSurface }]} numberOfLines={2}>
              {course.name}
            </Text>
            {course.professor ? (
              <Text style={[styles.professor, { color: theme.colors.onSurfaceVariant }]}>{course.professor}</Text>
            ) : null}
            <View style={styles.chipRow}>
              {dayText(course) ? <Chip compact style={styles.chip}>{dayText(course)}</Chip> : null}
            </View>
          </View>
          <View style={styles.statusBadge}>
            {course.reviewed ? (
              <View style={styles.reviewedBadge}>
                <Ionicons name="checkmark-circle" size={22} color={completedAccentColor} />
                <Text style={[styles.reviewedText, { color: completedAccentColor }]}>{t("投稿済み")}</Text>
              </View>
            ) : (
              <View style={styles.pendingBadge}>
                <Ionicons name="ellipse-outline" size={22} color={theme.colors.onSurfaceVariant} />
                <Text style={[styles.pendingText, { color: theme.colors.onSurfaceVariant }]}>{t("未投稿")}</Text>
              </View>
            )}
          </View>
        </View>

        <View style={styles.actionRow}>
          <Button
            mode={course.reviewed ? 'outlined' : 'contained'}
            compact
            onPress={() => {
              if (!isProfileComplete) {
                handleProfileIncompleteAlert();
              } else {
                navigation.navigate('CourseReviewForm', { course });
              }
            }}
            style={styles.actionButton}
          >
            {course.reviewed ? t("書き直す") : t("レビューを書く")}
          </Button>

          {isGradePeriod && course.reviewed && !course.gradeReported ? (
            <Button mode="outlined" compact onPress={() => navigation.navigate('GradeSubmit', { course })} style={styles.actionButton}>
              {t("成績を入力")}
            </Button>
          ) : null}

          {course.gradeReported ? (
            <Chip
              compact
              showSelectedCheck={false}
              style={[styles.chip, { backgroundColor: completedContainerColor }]}
              textStyle={{ color: completedContentColor }}
            >
              {t("成績入力済み")}
            </Chip>
          ) : null}
        </View>
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View style={[styles.centered, { backgroundColor: theme.colors.background }]}> 
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={{ marginTop: 12, color: theme.colors.onSurfaceVariant }}>{t("読み込み中...")}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]} contentContainerStyle={styles.content}>
      {!isProfileComplete ? (
        <Card style={[styles.headerCard, { backgroundColor: theme.colors.errorContainer }]}>
          <Card.Content>
            <Text style={{ color: theme.colors.onErrorContainer, fontWeight: '700' }}>
              {t("学年・学部が未設定のためレビューを投稿できません")}
            </Text>
            <Text style={{ color: theme.colors.onErrorContainer, marginTop: 4 }}>
              {t("設定画面で学年・学部を登録してください。")}
            </Text>
            <Button mode="contained" style={{ marginTop: 12 }} onPress={handleGoToProfileSettings}>
              {t("設定へ移動")}
            </Button>
          </Card.Content>
        </Card>
      ) : null}

      <Card style={[styles.headerCard, { backgroundColor: isAllComplete ? completedContainerColor : theme.colors.primaryContainer }]}>
        <Card.Content>
          <Text style={[styles.headerTitle, { color: isAllComplete ? completedContentColor : theme.colors.onPrimaryContainer }]}>
            {isAllComplete ? t("条件達成！") : t("{0}年度{1}の情報を投稿してください", [academicYear, t(semester)])}
          </Text>
          <Text style={[styles.headerSub, { color: isAllComplete ? completedContentColor : theme.colors.onPrimaryContainer }]}>
            {isAllComplete
              ? t("必要なレビューと成績がすべて投稿されました。")
              : t("授業評価の閲覧・履修検索を利用するには、以下の投稿が必要です。")}
          </Text>
          
          <View style={{ marginTop: 12 }}>
            <Text style={{ fontSize: 13, marginBottom: 4, color: isAllComplete ? completedContentColor : theme.colors.onPrimaryContainer, fontWeight: 'bold' }}>{t("講義レビュー")}</Text>
            <View style={styles.progressRow}>
              {renderProgressBar(progressRatio)}
              <Text style={[styles.progressLabel, { color: progressRatio >= 1 ? completedAccentColor : theme.colors.onPrimaryContainer }]}> 
                {localReviewCount} / {requiredCount} {t("件")}
              </Text>
            </View>
          </View>

          {isGradePeriod ? (
            <View style={{ marginTop: 12 }}>
              <Text style={{ fontSize: 13, marginBottom: 4, color: isAllComplete ? completedContentColor : theme.colors.onPrimaryContainer, fontWeight: 'bold' }}>{t("成績入力")}</Text>
              <View style={styles.progressRow}>
                {renderProgressBar(gradeProgressRatio)}
                <Text style={[styles.progressLabel, { color: gradeProgressRatio >= 1 ? completedAccentColor : theme.colors.onPrimaryContainer }]}>
                  {localGradeCount} / {requiredGradeCount} {t("件")}
                </Text>
              </View>
            </View>
          ) : null}
        </Card.Content>
      </Card>

      {enrolledCourses.length === 0 ? (
        <Card style={styles.emptyCard}>
          <Card.Content style={styles.emptyContent}>
            <Text style={[styles.emptyText, { color: theme.colors.onSurface }]}>{t("履修中の授業が見つかりませんでした")}</Text>
          </Card.Content>
        </Card>
      ) : (
        enrolledCourses.map(renderCourseCard)
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 16, paddingBottom: 40, gap: 12 },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  headerCard: { borderRadius: 16 },
  headerTitle: { fontSize: 17, fontWeight: '700' },
  headerSub: { marginTop: 8, fontSize: 13, lineHeight: 20 },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 12 },
  progressBarTrack: { flex: 1, height: 10, borderRadius: 5, backgroundColor: 'rgba(0,0,0,0.1)', overflow: 'hidden' },
  progressBarFill: { height: '100%', borderRadius: 5 },
  progressLabel: { fontSize: 14, fontWeight: '700', minWidth: 60, textAlign: 'right' },
  courseCard: { borderRadius: 12 },
  courseHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  courseInfo: { flex: 1, marginRight: 10 },
  courseName: { fontSize: 15, fontWeight: '700', marginBottom: 2 },
  professor: { fontSize: 13, marginBottom: 6 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  chip: { marginRight: 4 },
  statusBadge: { alignItems: 'center', minWidth: 60 },
  reviewedBadge: { alignItems: 'center' },
  reviewedText: { fontSize: 11, color: '#4CAF50', fontWeight: '600', marginTop: 2 },
  pendingBadge: { alignItems: 'center' },
  pendingText: { fontSize: 11, marginTop: 2 },
  actionRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  actionButton: { borderRadius: 8 },
  emptyCard: { borderRadius: 16 },
  emptyContent: { alignItems: 'center', paddingVertical: 24 },
  emptyText: { fontSize: 15, fontWeight: '600', textAlign: 'center' },
});

export default ReviewManagementScreen;
